import {
    DecimalRange,
    ExtendedApartmentBuilding,
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
    getExtendedModelPriceRange,
    PriceType,
    PropertyRentalPrice,
    PropertySalesPrice,
    showPrice,
} from '@bpd-library/utilities';
import { isGermany } from '@utilities/is-germany';

import { useTranslate } from '../hooks';

interface FormattedPrice {
    prefix?: string | Text;
    amount?: string | Text;
    suffix?: string | Text;
}

interface ExtendedModels {
    constructionNumber?: ExtendedConstructionNumber;
    apartmentBuilding?: ExtendedApartmentBuilding;
    propertyType?: ExtendedPropertyType;
    projectPhase?: ExtendedProjectPhase;
}

type PriceTypes = (PriceType | undefined)[];

const formatNumber = (number: number) =>
    Number(number).toLocaleString('nl-NL', { minimumFractionDigits: 0 });

const formatAmount = (amount: DecimalRange): string | undefined => {
    const { from, to } = amount;

    if (!from) return;

    let price = `€ ${formatNumber(from)}`;

    if (to && to !== from) {
        price += ` - € ${formatNumber(to)}`;
    }

    return price;
};

const isSamePriceType = (types: PriceTypes | undefined, keys: PriceTypes) =>
    types?.length && types?.every((priceType) => priceType && keys.includes(priceType));

const getApartmentBuildingPriceTypes = (
    apartmentBuilding: ExtendedApartmentBuilding,
): PriceTypes | undefined => {
    const { constructionNumbers, propertyTypes } = apartmentBuilding;

    const items = constructionNumbers?.length
        ? constructionNumbers
        : (propertyTypes as (ExtendedConstructionNumber | ExtendedPropertyType)[] | undefined);

    if (items) {
        return items
            .filter(({ status }) => status !== 'Sold' && status !== 'UnderConstruction')
            ?.map(({ salesPrice }) => salesPrice?.priceType);
    }

    return;
};

const getApartmentBuildingUniquePrices = (
    apartmentBuilding: ExtendedApartmentBuilding,
): DecimalRange[] => {
    const { constructionNumbers, propertyTypes } = apartmentBuilding;

    const items = constructionNumbers?.length
        ? constructionNumbers
        : (propertyTypes as (ExtendedConstructionNumber | ExtendedPropertyType)[] | undefined);

    return items
        ? (items
              .map((item) => item.salesPrice?.amount || item.rentalPrice?.amount)
              .reduce<DecimalRange[]>((amounts, amount) => {
                  if (!amount) return amounts;

                  const isUnique = !amounts.find(
                      (amountB) => amountB?.from === amount.from && amountB?.to === amount.to,
                  );

                  if (isUnique) amounts.push(amount);

                  return amounts;
              }, []) as DecimalRange[])
        : [];
};

const getPriceTypeAbbreviation = (
    priceType: PriceType | undefined,
    amount?: DecimalRange,
    isCN?: boolean,
): Text | undefined => {
    if (amount?.from && amount.to && isCN) return;
    if (priceType === 'StartsFrom') return useTranslate('Acronym-Vanaf');
    if (priceType === 'Indication' || priceType === 'Circa') return useTranslate('Acronym-Circa');

    return;
};

const hasExclusivePrice = (
    apiObject: ExtendedApartmentBuilding | ExtendedPropertyType,
): boolean => {
    if (!apiObject.constructionNumbers?.length) return false;

    /**
     * If every non-sold construction number that can show price has the price as `Kosten Koper`,
     * then the passed object also has an exclusive price suffix
     * */
    return !!apiObject.constructionNumbers
        .filter((cn) => !['Rented', 'Sold', 'UnderConstruction'].includes(cn.status || ''))
        .filter((cn) => showPrice({ constructionNumber: cn }))
        .every((cn) => cn.salesPrice?.condition === 'KostenKoper');
};

const formatPurchasePrice = (
    { amount, condition, priceType }: PropertySalesPrice,
    isCN?: boolean,
): FormattedPrice | undefined => {
    if (!amount) return;

    const formattedPrice: FormattedPrice = {
        prefix: getPriceTypeAbbreviation(priceType, amount, isCN),
        amount: formatAmount(amount),
    };

    const priceOnRequest = priceType === 'PriceOnRequest';

    if (priceOnRequest) {
        formattedPrice.amount = useTranslate('Acronym-PriceOnRequest');
    } else {
        if (condition === 'VrijOpNaam') formattedPrice.suffix = useTranslate('Acronym-Inclusive');
        if (condition === 'KostenKoper') formattedPrice.suffix = useTranslate('Acronym-Exclusive');
    }

    return formattedPrice;
};

const formatRentalPrice = ({
    amount,
    priceType,
}: PropertyRentalPrice): FormattedPrice | undefined => {
    if (!amount) return;

    return {
        prefix: getPriceTypeAbbreviation(priceType),
        amount: formatAmount(amount),
        suffix: 'p.m.',
    };
};

/**
 * @description Returns the correct prefix, suffix and price display name
 * @param {ExtendedModels} {
 *     constructionNumber,
 *     apartmentBuilding,
 *     propertyType,
 * }
 * @return {*}  {(FormattedPrice | undefined)}
 */
const formatPrice = ({
    constructionNumber,
    apartmentBuilding,
    propertyType,
    projectPhase,
}: ExtendedModels): FormattedPrice | undefined => {
    const { status } = constructionNumber || apartmentBuilding || propertyType || {};

    const canShowPrice = !['Rented', 'Sold', 'UnderConstruction'].includes(status || '');

    if (!canShowPrice) {
        if (isGermany()) return;
        if (constructionNumber?.projectPhase?.status !== 'OnSale') return;
    }

    if (constructionNumber) {
        const { salesPrice, rentalPrice } = constructionNumber;

        if (salesPrice) return formatPurchasePrice(salesPrice, true);
        if (rentalPrice) return formatRentalPrice(rentalPrice);
    }

    if (propertyType) {
        const { salesPrice, rentalPrice } = propertyType;

        if (salesPrice) {
            if (hasExclusivePrice(propertyType)) salesPrice.condition = 'KostenKoper';
            return formatPurchasePrice(salesPrice);
        }

        if (rentalPrice) return formatRentalPrice(rentalPrice);
    }

    if (apartmentBuilding) {
        const { projectPhase } = apartmentBuilding;
        const price = getExtendedModelPriceRange(apartmentBuilding);
        const priceTypes = getApartmentBuildingPriceTypes(apartmentBuilding);
        const uniquePrices = getApartmentBuildingUniquePrices(apartmentBuilding);

        const isRental = !!projectPhase?.isRental;
        const hasVariousPrices = uniquePrices.length > 1;
        const hasVariousPriceTypes = [...new Set(priceTypes)].length > 1;

        /**
         * When only the pricetype ‘Prijs op aanvraag’ is used in 1 alias,
         * then show no prefix nor price, but ‘prijs op aanvraag’
         * */

        if (isSamePriceType(priceTypes, ['PriceOnRequest'])) {
            return {
                amount: useTranslate('Acronym-PriceOnRequest'),
            };
        }

        if (!price) return;

        let prefix: Text | undefined;

        /**
         * When all the construction number prices whithin 1 alias has c.a.,
         * i see ca as a prefix before the price
         * */

        if (isSamePriceType(priceTypes, ['Circa', 'Indication']))
            prefix = useTranslate('Acronym-Circa');

        if (isSamePriceType(priceTypes, ['StartsFrom'])) prefix = useTranslate('Acronym-Vanaf');

        /**
         * When there are multiple price variations, Vanaf is shown by default
         * */

        if (hasVariousPrices || hasVariousPriceTypes) prefix = useTranslate('Acronym-Vanaf');

        const suffixDictionaryKey = hasExclusivePrice(apartmentBuilding)
            ? 'Acronym-Exclusive'
            : 'Acronym-Inclusive';

        return {
            prefix,
            amount: hasVariousPrices ? formatAmount({ from: price.from }) : formatAmount(price),
            suffix: isRental ? 'p.m.' : useTranslate(suffixDictionaryKey),
        };
    }

    if (projectPhase) {
        const { propertyPurchasePrice, propertyRentalPrice } = projectPhase;
        if (propertyPurchasePrice) return formatPurchasePrice(propertyPurchasePrice);
        if (propertyRentalPrice) return formatRentalPrice(propertyRentalPrice);
    }

    return;
};

export { FormattedPrice, formatPrice };
